import React, { useEffect, useState } from 'react';
import { Layout } from '@/components/Layout/Layout';
import { SectionCard } from '@/components/common/SectionCard';
import { CommonCard } from '@/components/common/CommonCard';
import {
    BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
    DollarSign, TrendingUp, Store, CreditCard, Truck, XCircle, Users,
    Download, FileText, FileSpreadsheet, PieChart as PieIcon,
    RotateCcw, Banknote, RefreshCw, Star,
    Repeat, Zap, Package, ShoppingBag
} from 'lucide-react';
import { formatRand, GetFilterUrl } from '@/utility/helper';
import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { EmptyChart } from '@/components/common/EmptyChart';
import {
    CommonCardSkeleton, LineChartSkeleton, HorizontalBarChartSkeleton, DonutChartSkeleton
} from '@/components/ui/Loaders/DashboardSkeletons';
import { Range } from 'react-date-range';
import { FilterBar } from '@/components/common/FilterBar';
import { IAnalyticsData, IRevenueAndOrderTrend, IPaymentSplit, IStoreWiseEarning } from '@/interfaces/Dashboard/dashboard.interface';

type Section = 'earnings' | 'operations' | 'zones' | 'reports';

const SECTIONS = [
    { key: 'earnings' as Section, label: 'Earnings', icon: <DollarSign className="w-4 h-4" /> },
    { key: 'reports' as Section, label: 'Reports & Exports', icon: <Download className="w-4 h-4" /> },
];

const TIME_FILTERS = [
    { value: 'today', label: 'Today' },
    { value: 'weekly', label: 'This Week' },
    { value: 'monthly', label: 'This Month' },
    { value: 'yearly', label: 'This Year' },
    { value: 'custom', label: 'Custom Range' },
];

// ─── SHARED INTERFACES ────────────────────────────────────────────────────────

const TOOLTIP_STYLE = {
    contentStyle: {
        backgroundColor: 'hsl(var(--background))',
        borderColor: 'hsl(var(--border))',
        borderRadius: '10px',
        fontSize: 12,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    }
};

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function AnalyticsPage() {
    const [activeSection, setActiveSection] = useState<Section>('earnings');
    const [timeFilter, setTimeFilter] = useState('monthly');
    const [customDateRange, setCustomDateRange] = useState<Range[]>([{
        startDate: new Date(), endDate: new Date(), key: 'selection'
    }]);
    const [exportingKey, setExportingKey] = useState<string | null>(null);
    const [data, setData] = useState<IAnalyticsData | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchAnalytics = async () => {
            try {
                setIsLoading(true);
                const dashboardParams = GetFilterUrl({
                    ...(timeFilter && timeFilter !== 'custom' ? { filter: timeFilter } : {}),
                    ...(timeFilter === 'custom' && customDateRange[0].startDate && customDateRange[0].endDate ? {
                        filter: 'custom',
                        startDate: customDateRange[0].startDate.toISOString().split('T')[0],
                        endDate: customDateRange[0].endDate.toISOString().split('T')[0],
                    } : {}),
                });
                const res = await callApi(`${API_URL}${API_ENDPOINT.reportsAnalytics}?${dashboardParams}`, {
                    method: "GET"
                });
                if (res?.status) {
                    setData(res.data);
                }
            } catch (err) {
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchAnalytics();
    }, [timeFilter, customDateRange[0]?.startDate, customDateRange[0]?.endDate]);

    const kpis = data?.kpis || {};
    const revenueBreakdown = data?.revenueBreakdown || {};
    const charts = data?.charts || {};

    const FINANCIAL_KPIS = {
        grossRevenue: kpis.totalEarnings || 0,
        avgOrderValue: kpis.avgOrderValue || 0,
        netRevenue: revenueBreakdown.netRevenue || 0,
        codPending: revenueBreakdown.cashReceived || 0,
        refunds: revenueBreakdown.refundDeductions || 0,
        deliveryCharges: revenueBreakdown.storeOrdersValue || 0,
        taxes: revenueBreakdown.taxesCollected || 0,
        discounts: revenueBreakdown.orderCancelled || 0,
    };

    const OPERATIONAL_KPIS = {
        totalOrders: kpis.totalOrders || 0,
        customerSatisfaction: kpis.customerSatisfaction || 0,
        repeatCustomers: revenueBreakdown.orderCancelledPercentage || 0,
        peakHour: revenueBreakdown.mostOrdersTime || 'N/A',
    };

    const REVENUE_TREND = (charts.revenueAndOrderTrend || []).map((item: IRevenueAndOrderTrend) => {
        let label = '';
        if (timeFilter === 'today' && item.hour !== undefined) {
            const hr = parseInt(item.hour, 10);
            if (!isNaN(hr)) {
                const ampm = hr >= 12 ? 'PM' : 'AM';
                const displayHr = hr % 12 === 0 ? 12 : hr % 12;
                label = `${displayHr} ${ampm}`;
            } else {
                label = `${item.hour}:00`;
            }
        } else {
            const dateStr = item.date || item.month;
            const date = dateStr ? new Date(dateStr) : null;
            if (date && !isNaN(date.getTime())) {
                label = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
            } else {
                label = item.hour ? `${item.hour}:00` : '';
            }
        }
        return {
            label,
            revenue: item.earning || 0,
            orders: item.orderCount || 0,
        };
    });

    // Calculate total for percentages
    const totalPayments = (charts.paymentSplit || []).reduce((acc: number, curr: IPaymentSplit) => acc + (curr.count || 0), 0);
    const PAYMENT_METHODS = (charts.paymentSplit || []).map((item: IPaymentSplit, i: number) => {
        const colors = ['#F59E0B', '#6366F1', '#10B981', '#8B5CF6'];
        return {
            method: item.paymentMethod?.toUpperCase() || 'Unknown',
            amount: item.count || 0,
            percentage: totalPayments ? Math.round(((item.count || 0) / totalPayments) * 100) : 0,
            color: colors[i % colors.length]
        };
    });

    const STORE_EARNINGS = (charts.storeWiseEarnings || []).map((item: IStoreWiseEarning) => ({
        storeName: item.storeName || 'Unknown Store',
        revenue: item.revenue || 0,
        orders: item.orderCount || 0
    }));

    const DELIVERY_ZONES: { zone: string; avgTime: number; orders: number; success: number }[] = [];
    const CANCELLATION_REASONS: { reason: string; count: number; percentage: number; color: string; source: string }[] = [];
    const DRIVER_STATS: { name: string; orders: number; earnings: number; avgTime: number; acceptance: number; rating: number }[] = [];
    const DELIVERY_STATUS: { name: string; value: number; color: string }[] = [];
    const ORDER_FUNNEL: { stage: string; count: number }[] = [];
    const WEEKLY_COMPARISON: { week: string; thisMonth: number; lastMonth: number }[] = [];

    const currentDate = new Date().toLocaleDateString('en-ZA', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });

    const handleMockExport = (key: string) => {
        setExportingKey(key);
        setTimeout(() => setExportingKey(null), 1800);
    };



    return (
        <Layout>
            <div className="space-y-6 pb-24 animate-in fade-in duration-500">

                {/* ── PAGE HEADER ─────────────────────────────────────────── */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground">Analytics & Reports</h1>
                        <p className="text-sm text-muted-foreground mt-0.5">Yebo Eggs · {currentDate}</p>
                    </div>
                </div>

                <FilterBar
                    activeFilter={timeFilter}
                    setActiveFilter={setTimeFilter}
                    customDateRange={customDateRange}
                    setCustomDateRange={setCustomDateRange}
                    selectedStoreId={''}
                    setSelectedStoreId={() => { }}
                    selectedStatus={''}
                    setSelectedStatus={() => { }}
                    stores={[]}
                />

                {isLoading || !data ? (
                    <div className="space-y-6 animate-pulse">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {Array.from({ length: 4 }).map((_, i) => (
                                <CommonCardSkeleton key={i} />
                            ))}
                        </div>
                        <div className="flex gap-1 border-b border-border">
                            <div className="h-10 bg-zinc-200 rounded w-28" />
                            <div className="h-10 bg-zinc-200 rounded w-28" />
                        </div>
                        <div className="space-y-6">
                            <div>
                                <div className="h-4 bg-zinc-200 rounded w-36 mb-3" />
                                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-3">
                                    {Array.from({ length: 8 }).map((_, i) => (
                                        <CommonCardSkeleton key={i} />
                                    ))}
                                </div>
                            </div>
                            <div className="grid grid-cols-1 gap-6">
                                <SectionCard title="Revenue & Order Trend" icon={<TrendingUp />}>
                                    <div className="h-72 pt-2">
                                        <LineChartSkeleton />
                                    </div>
                                </SectionCard>
                            </div>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <SectionCard title="Store-wise Earnings" icon={<Store />}>
                                    <div className="h-64 pt-2">
                                        <HorizontalBarChartSkeleton />
                                    </div>
                                </SectionCard>
                                <SectionCard title="Payment Split" icon={<CreditCard />}>
                                    <div className="h-[23.5rem] pt-2 flex flex-col justify-center">
                                        <DonutChartSkeleton />
                                    </div>
                                </SectionCard>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* ── SUMMARY STRIP ───────────────────────────────────────── */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {[
                                { label: 'Gross Revenue', value: formatRand(FINANCIAL_KPIS.grossRevenue), icon: <DollarSign />, color: '#10B981', change: 14 },
                                { label: 'Total Orders', value: OPERATIONAL_KPIS.totalOrders, icon: <ShoppingBag />, color: '#6366F1', change: 8 },
                                // { label: 'Avg Order Value', value: formatRand(FINANCIAL_KPIS.avgOrderValue), icon: <ShoppingCart />, color: '#F59E0B', change: -3 },
                                { label: 'Customer Satisfaction', value: `${OPERATIONAL_KPIS.customerSatisfaction}/5 ⭐`, icon: <Star />, color: '#FBBF24', change: 5 },
                            ].map((kpi, i) => (
                                <div key={i} className="bg-card border border-border rounded-xl p-4 flex flex-col gap-3 shadow-sm hover:shadow-md transition-shadow">
                                    <div className="flex items-center justify-between">
                                        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${kpi.color}15`, color: kpi.color }}>
                                            {kpi.icon}
                                        </div>
                                        {/* <StatBadge value={kpi.change} suffix="%" /> */}
                                    </div>
                                    <div>
                                        <p className="text-xs text-muted-foreground">{kpi.label}</p>
                                        <p className="text-xl font-bold text-foreground mt-0.5">{kpi.value}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* ── SECTION TABS ────────────────────────────────────────── */}
                        <div className="flex gap-1 border-b border-border">
                            {SECTIONS.map(s => (
                                <button
                                    key={s.key}
                                    onClick={() => setActiveSection(s.key)}
                                    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-all rounded-t-md
                                ${activeSection === s.key
                                            ? 'border-primary text-primary bg-primary/5'
                                            : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary/40'}`}
                                >
                                    {s.icon}{s.label}
                                </button>
                            ))}
                        </div>

                        {/* 
                ══════════════════════════════════════════════════════════
                TAB 1 — EARNINGS ANALYTICS
                ══════════════════════════════════════════════════════════
                 */}
                        {activeSection === 'earnings' && (
                            <div className="space-y-6 animate-in fade-in duration-300">

                                {/* Financial KPI Row */}
                                <div>
                                    <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
                                        Revenue Breakdown
                                    </h2>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-3">
                                        <CommonCard label="Net Revenue" value={formatRand(FINANCIAL_KPIS.netRevenue)} icon={<TrendingUp />} iconColor="#059669" />
                                        <CommonCard label="COD Pending" value={formatRand(FINANCIAL_KPIS.codPending)} icon={<Banknote />} iconColor="#F59E0B" />
                                        <CommonCard label="Refund Deductions" value={formatRand(FINANCIAL_KPIS.refunds)} icon={<RotateCcw />} iconColor="#EF4444" />
                                        <CommonCard label="Delivery Charges" value={formatRand(FINANCIAL_KPIS.deliveryCharges)} icon={<Truck />} iconColor="#3B82F6" />
                                        <CommonCard label="Taxes Collected" value={formatRand(FINANCIAL_KPIS.taxes)} icon={<FileText />} iconColor="#6B7280" />
                                        <CommonCard label="Discounts Given" value={formatRand(FINANCIAL_KPIS.discounts)} icon={<XCircle />} iconColor="#8B5CF6" />
                                        <CommonCard label="Repeat Customers" value={`${OPERATIONAL_KPIS.repeatCustomers}%`} icon={<Repeat />} iconColor="#6366F1" />
                                        <CommonCard label="Peak Order Hour" value={OPERATIONAL_KPIS.peakHour} icon={<Zap />} iconColor="#F97316" />
                                    </div>
                                </div>

                                {/* Revenue Area Chart + Payment Method */}
                                <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
                                    <div className="lg:col-span-2">
                                        <SectionCard title="Revenue & Order Trend" icon={<TrendingUp />}>
                                            <div className="h-72 pt-2">
                                                {REVENUE_TREND.length > 0 ? (
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <AreaChart data={REVENUE_TREND} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                                                            <defs>
                                                                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                                                                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.15} />
                                                                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                                                                </linearGradient>
                                                                <linearGradient id="ordGrad" x1="0" y1="0" x2="0" y2="1">
                                                                    <stop offset="5%" stopColor="#6366F1" stopOpacity={0.12} />
                                                                    <stop offset="95%" stopColor="#6366F1" stopOpacity={0} />
                                                                </linearGradient>
                                                            </defs>
                                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                                                            <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 11, opacity: 0.6 }} interval={0} angle={-45} textAnchor="end" height={60} />
                                                            <YAxis yAxisId="rev" axisLine={false} tickLine={false} tick={{ fontSize: 11, opacity: 0.6 }} tickFormatter={v => v >= 1000 ? `R${(v / 1000).toFixed(0)}k` : `R${v}`} />
                                                            <YAxis yAxisId="ord" orientation="right" axisLine={false} tickLine={false} tick={{ fontSize: 11, opacity: 0.6 }} />
                                                            <Tooltip {...TOOLTIP_STYLE} formatter={(v: number | string, name: string) => [name === 'revenue' ? formatRand(Number(v)) : v, name === 'revenue' ? 'Revenue' : 'Orders']} />
                                                            <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                                                            <Area yAxisId="rev" type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2.5} fill="url(#revGrad)" name="revenue" dot={false} activeDot={{ r: 5 }} />
                                                            <Area yAxisId="ord" type="monotone" dataKey="orders" stroke="#6366F1" strokeWidth={2} fill="url(#ordGrad)" name="orders" dot={false} activeDot={{ r: 4 }} strokeDasharray="5 3" />
                                                        </AreaChart>
                                                    </ResponsiveContainer>
                                                ) : (
                                                    <EmptyChart height="h-72" />
                                                )}
                                            </div>
                                        </SectionCard>
                                    </div>

                                    {/* Payment Method Donut */}

                                </div>

                                {/* Store Earnings + Weekly Comparison */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    <SectionCard title="Store-wise Earnings" icon={<Store />}>
                                        <div className="h-64 pt-2">
                                            {STORE_EARNINGS.length > 0 ? (
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart layout="vertical" data={STORE_EARNINGS} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                                                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(var(--border))" />
                                                        <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 11, opacity: 0.6 }} tickFormatter={v => v >= 1000 ? `R${(v / 1000).toFixed(0)}k` : `R${v}`} />
                                                        <YAxis type="category" dataKey="storeName" axisLine={false} tickLine={false} tick={{ fontSize: 11, opacity: 0.7 }} width={95} />
                                                        <Tooltip {...TOOLTIP_STYLE} formatter={(v: number | string) => [formatRand(Number(v)), 'Revenue']} />
                                                        <Bar dataKey="revenue" fill="#6366F1" radius={[0, 4, 4, 0]} />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            ) : (
                                                <EmptyChart height="h-64" />
                                            )}
                                        </div>
                                    </SectionCard>
                                    <SectionCard title="Payment Split" icon={<CreditCard />}>
                                        {PAYMENT_METHODS.length > 0 ? (
                                            <>
                                                <div className="h-44">
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <PieChart>
                                                            <Pie data={PAYMENT_METHODS} cx="50%" cy="50%" outerRadius={75} innerRadius={45} paddingAngle={3} dataKey="amount">
                                                                {PAYMENT_METHODS.map((p, i) => <Cell key={i} fill={p.color} />)}
                                                            </Pie>
                                                            <Tooltip {...TOOLTIP_STYLE} formatter={(v: number | string) => [formatRand(Number(v)), '']} />
                                                        </PieChart>
                                                    </ResponsiveContainer>
                                                </div>
                                                <div className="space-y-2 mt-1">
                                                    {PAYMENT_METHODS.map((p, i) => (
                                                        <div key={i} className="flex items-center gap-2">
                                                            <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: p.color }} />
                                                            <span className="text-[11px] text-muted-foreground flex-1">{p.method}</span>
                                                            <div className="w-24 h-2 bg-secondary/30 rounded-full overflow-hidden">
                                                                <div className="h-full rounded-full" style={{ width: `${p.percentage}%`, backgroundColor: p.color }} />
                                                            </div>
                                                            <span className="text-[11px] font-semibold w-7 text-right">{p.percentage}%</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        ) : (
                                            <EmptyChart height="h-64" />
                                        )}
                                    </SectionCard>
                                </div>
                            </div>
                        )}
                        {/* ══════════════════════════════════════════════════════════
                    TAB 4 — REPORTS & EXPORTS
                ══════════════════════════════════════════════════════════ */}
                        {activeSection === 'reports' && (
                            <div className="space-y-6 animate-in fade-in duration-300">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm text-muted-foreground">
                                        Downloads will apply your selected time filter: <span className="font-semibold text-foreground">{TIME_FILTERS.find(f => f.value === timeFilter)?.label}</span>
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {[
                                        { key: 'orders', title: 'Orders Report', desc: 'All orders with revenue, status and payment breakdown', icon: <ShoppingBag className="w-6 h-6" />, color: '#10B981', rows: '1,248 records' },
                                        { key: 'revenue', title: 'Revenue Report', desc: 'Gross & net revenue, taxes, delivery charges and discounts', icon: <DollarSign className="w-6 h-6" />, color: '#059669', rows: '412 records' },
                                        { key: 'drivers', title: 'Driver Payout Report', desc: 'Driver earnings, delivery count and payout summary', icon: <Truck className="w-6 h-6" />, color: '#3B82F6', rows: '12 records' },
                                        { key: 'stores', title: 'Store Performance Report', desc: 'Store-wise orders, revenue and operational data', icon: <Store className="w-6 h-6" />, color: '#8B5CF6', rows: '5 records' },
                                        { key: 'returns', title: 'Return & Refund Report', desc: 'All return requests with refund status and amounts', icon: <RotateCcw className="w-6 h-6" />, color: '#EF4444', rows: '87 records' },
                                        { key: 'coupons', title: 'Coupon Usage Report', desc: 'Coupon redemptions, discount totals and usage trends', icon: <FileText className="w-6 h-6" />, color: '#F59E0B', rows: '234 records' },
                                        { key: 'products', title: 'Products Report', desc: 'Full product catalog with stock levels and sales data', icon: <Package className="w-6 h-6" />, color: '#6366F1', rows: '156 records' },
                                        { key: 'settlements', title: 'Settlement Report', desc: 'Store payouts, COD collections and wallet balances', icon: <FileSpreadsheet className="w-6 h-6" />, color: '#06B6D4', rows: '5 records' },
                                        { key: 'customers', title: 'Customer Report', desc: 'Customer activity, order history and spend analysis', icon: <Users className="w-6 h-6" />, color: '#F97316', rows: '2,341 records' },
                                    ].map((report) => (
                                        <div key={report.key} className="bg-card border border-border rounded-xl p-5 hover:shadow-md hover:border-primary/20 transition-all group">
                                            <div className="flex items-start gap-4 mb-4">
                                                <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-105"
                                                    style={{ backgroundColor: `${report.color}15`, color: report.color }}>
                                                    {report.icon}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="font-semibold text-foreground text-sm">{report.title}</h3>
                                                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{report.desc}</p>
                                                    <span className="text-[10px] text-muted-foreground/60 mt-1 block">{report.rows}</span>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleMockExport(report.key)}
                                                disabled={exportingKey === report.key}
                                                className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold border border-border hover:bg-secondary/60 transition-colors disabled:opacity-60 cursor-pointer"
                                            >
                                                {exportingKey === report.key ? (
                                                    <>
                                                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                                        Generating...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Download className="w-3.5 h-3.5" />
                                                        Export Excel
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}

            </div>
        </Layout>
    );
}
